/* PLUGINS */
import { LucideIcon, X } from "lucide-react";
import { ControllerRenderProps, FieldValues, Path } from "react-hook-form";
import { useCallback, useRef, MutableRefObject } from "react";

/* COMPONENTS */
import {
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

interface FormInputProps<TFieldValues extends FieldValues> {
  field: ControllerRenderProps<TFieldValues, Path<TFieldValues>>;
  label: string;
  type?: "text" | "email" | "tel";
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  icon?: LucideIcon;
  allowClear?: boolean;
  onClear?: () => void;
}

/**
 * DOCU: Reusable form input component with optional icon, clear button, and label. <br>
 * Triggered: Used in registration and login forms for text inputs. <br>
 * Last Updated Date: December 1, 2025 <br>
 * @param field - React Hook Form field props.
 * @param label - Label text for input.
 * @param type - Input type (text, email, tel).
 * @param placeholder - Optional placeholder text.
 * @param disabled - Whether input is disabled.
 * @param required - Whether to show required asterisk.
 * @param icon - Optional Lucide icon component to display on the left.
 * @param allowClear - Whether to show a clear button when input has value.
 * @param onClear - Optional callback when clear button is clicked.
 * @author Development Team
 */
export function FormInput<TFieldValues extends FieldValues>({
  field,
  label,
  type = "text",
  placeholder = "",
  disabled = false,
  required = false,
  icon: Icon,
  allowClear = false,
  onClear,
}: FormInputProps<TFieldValues>) {
  const input_id = `input-${field.name}`;
  const hasValue = typeof field.value === "string" && field.value.trim() !== "";
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Merge refs: both the field ref and our local ref
  const mergedRef = useCallback(
    (element: HTMLInputElement | null) => {
      inputRef.current = element;
      if (typeof field.ref === "function") {
        field.ref(element);
      } else if (field.ref) {
        (field.ref as MutableRefObject<HTMLInputElement | null>).current =
          element;
      }
    },
    [field.ref],
  );

  const handleClear = () => {
    field.onChange("");
    onClear?.();
    inputRef.current?.focus();
  };

  return (
    <FormItem>
      <FormLabel
        htmlFor={input_id}
        className="block text-foreground mb-0 font-normal text-base"
      >
        {label}{" "}
        {required ? (
          <span className="text-destructive" aria-hidden="true">
            *
          </span>
        ) : (
          <span className="text-muted-foreground text-sm">(Optional)</span>
        )}
      </FormLabel>
      <FormControl>
        <div className="relative">
          {Icon && (
            <Icon
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-5 h-5"
              aria-hidden="true"
            />
          )}
          <Input
            {...field}
            ref={mergedRef}
            id={input_id}
            type={type}
            placeholder={placeholder}
            disabled={disabled}
            aria-required={required}
            className={`w-full ${Icon ? "pl-10" : "px-4"} ${
              allowClear && hasValue ? "pr-10" : "pr-4"
            } py-3 border-2 border-border rounded-lg focus-visible:ring-1 focus-visible:ring-brand focus-visible:border-brand transition-colors`}
          />
          {allowClear && hasValue && !disabled && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleClear}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Clear input"
            >
              <X className="w-4 h-4" aria-hidden="true" />
            </button>
          )}
        </div>
      </FormControl>
      <FormMessage className="mt-1 text-sm text-destructive" role="alert" />
    </FormItem>
  );
}
